import { Component, Fragment, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import styled from 'styled-components';
import { debounce } from 'lodash';
import Button from 'src/components/buttons/button';
import UserLink from 'src/modules/shared/links/userLink';
import Respond from './respond';
import LikeButton from 'src/components/buttons/likeButton';
import ActionsDropdown from './actionsDropdown';
import MessageEditor from 'src/components/editor/messageEditor';
import { getElement } from 'src/utils/parserHTML';
import { showSignupModal, showModal } from 'src/modules/shared/modal/actions';
import signupContext from 'src/constants/signupContext';
import { userHasRole, userHasPermission } from 'src/utils/context';
import { editPostReply } from 'src/modules/community/singlePost/actions/actions';
import i18n from 'src/utils/translate';
import { trackFootfallEvent } from 'src/utils/footfall';
import footfallEventTypes from 'src/constants/footfallEventTypes';
import ModalType from 'src/constants/modalTypes';
import { textLinkPrimary } from 'styles-js/mixins/typography';
import ResponseHeader from './responseHeader';
import PostImage from 'src/pages/community/singlePost/postImage';
import dynamic from 'next/dynamic';
const EmojiPicker = dynamic(() => import('./emojiPicker'), { ssr: false });
import RespondImageUpload from 'src/modules/community/singlePost/components/respondImageUpload';
import { showGuidelines } from 'src/modules/community/shared/components/communityGuidelinesModal';
import { EMAIL, PHONE_NUMBER } from 'src/constants/regex';

export class PostResponse extends Component {

  static propTypes = {
    indent: PropTypes.number.isRequired,
    response: PropTypes.object.isRequired,
    superPost: PropTypes.object.isRequired,
    signupContext: PropTypes.object.isRequired,
  }

  static defaultProps = {
    signupContext: {
      section: signupContext.section.REPLY,
    },
  }

  constructor(props) {
    super(props);
    this.state = {
      bodyEdited: false,
      responding: false,
      editing: false,
      showEmojiPicker: false,
      submitDisabled: false,
      body: props.response.body || '',
      bodyValidations: [],
      mentionedUsers: [],
      previewImageHash: props.response.postImageHash || '',
      previewImageUrl: props.response.imagePreview || null,
      previewImageDescription: props.response.imageDescription || '',
    };
    this.messageEditorRef = createRef();
  }

  onReplyClick = () => {
    const { currentUser, showSignupModal } = this.props;
    const isActive = !!currentUser;
    if (!isActive) {
      const trackingContext = {
        section: this.props.signupContext.section,
        element: signupContext.element.REPLY,
      };
      trackFootfallEvent(footfallEventTypes.CLICKED, {
        clickType: 'sign-up',
        clickMetadata: trackingContext,
      });
      showSignupModal(trackingContext);
    } else {
      this.setState({ responding: true });
    }
  }

  onReportClick = () => {
    const { currentUser, response, showModal, showSignupModal } = this.props;
    if (currentUser) {
      trackFootfallEvent(footfallEventTypes.CLICKED, {
        clickType: 'report',
        clickMetadata: { section: 'reply' },
      });
      showModal({
        modalType: ModalType.REPORT,
        modalProps: { postId: response.id },
      });
    } else {
      const reportContext = {
        element: signupContext.element.REPORT,
        section: signupContext.section.POST_REPLY,
      };
      trackFootfallEvent(footfallEventTypes.CLICKED, {
        clickType: 'sign-up',
        clickMetadata: reportContext,
      });
      showSignupModal(reportContext);
    }
  }

  onEdit = async (event) => {
    event.preventDefault();
    const { body, bodyValidations, mentionedUsers } = this.state;
    const { currentUser, community, response, superPost, showSignupModal, showModal } = this.props;

    if (!currentUser) {
      showSignupModal({
        section: this.props.signupContext.section,
        element: signupContext.element.EDIT,
      });
      return;
    }

    if (bodyValidations.length > 0) {
      return;
    }

    if (userHasRole(currentUser, 'Restricted')) {
      return;
    }

    // Make a response request
    const data = {
      body,
      communitySlug: community.slug,
      mentionedUsers,
      postId: response.id,
      superParentId: superPost.id,
      parentId: response.parentPostId ? response.parentPostId : superPost.id,
      successMessage: i18n.t('Response edited successfully!'),
      imageHash: this.state.previewImageHash,
      imageDescription: this.state.previewImageDescription,
    };

    if (EMAIL.test(body) || PHONE_NUMBER.test(body) || this.state.previewImageHash) {
      return showModal({
        modalType: ModalType.PRIVACY_REMINDER,
        modalProps: {
          onCancel: () => {},
          publishPost: this.editResponse,
          replyData: data,
        },
      });
    }
    this.setState({ showEmojiPicker: false });
    this.editResponse(data);
  }

  editResponse = async (data) => {
    await this.props.editPostReply(data);
    this.setState({ bodyEdited: false, editing: false });
    trackFootfallEvent(footfallEventTypes.CLICKED, {clickType: 'completed-reply'});
  }

  onBlurBody = () => {
    if (!this.messageEditorRef.current) return;
    const { str, mentionedUsers } = this.messageEditorRef.current.getData();
    this.setState({ body: str, mentionedUsers });
  }

  onChange = () => {
    if (!this.state.bodyEdited) this.setState({ bodyEdited: true });
    if (this.messageEditorRef.current && this.messageEditorRef.current.getData().str.trim() === this.props.response.body) {
      this.setState({ bodyEdited: false });
    }
  }

  startMention = () => {
    if (!this.messageEditorRef.current) return;
    this.messageEditorRef.current.startMention();
  }

  addEmoji = (emoji) => {
    if (!this.messageEditorRef.current) return;
    this.messageEditorRef.current.addEmoji(emoji);
    this.setState({ bodyEdited: true });
  }

  focusSelection = () => {
    this.messageEditorRef.current.focusNow();
    const sel = window.getSelection();
    const range = sel.getRangeAt(0);
    sel.addRange(range);
  }

  toggleFormat = (type) => {
    this.focusSelection();
    if (type) {
      document.execCommand(type, false, null);
      this.setState({ [type]: !this.state[type] });
    } else {
      this.setState({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
      });
    }
  }

  debouncedToggleFormat = debounce(this.toggleFormat, 200);

  renderEditBox() {
    const { response, community, currentUser, showModal } = this.props;
    const { showEmojiPicker, submitDisabled, bodyEdited, previewImageUrl, previewImageDescription } = this.state;

    return (
      <Fragment>
        <MessageEditor
          className="input--text"
          currentUserId={currentUser.userId}
          enrichedText={response.enriched.body}
          groupId={community.communityId}
          previewImageDescription={previewImageDescription}
          previewImageUrl={previewImageUrl}
          ref={this.messageEditorRef}
          onAddImageDescription={() => {
            showModal({
              modalType: ModalType.REPLY_IMAGE_DESCRIPTION,
              modalProps: {
                imageDescription: previewImageDescription,
                onAccept: (newDescription) => this.setState({ previewImageDescription: newDescription, bodyEdited: true }),
              },
            });
          }}
          onBlur={this.onBlurBody}
          onChange={this.onChange}
          onFocus={() => {
            if (showGuidelines(community)) {
              showModal({modalType: ModalType.COMMUNITY_GUIDELINES});
            }
          }}
          onPreviewRemove={() => this.setState({
            previewImageHash: '', previewImageUrl: null, previewImageDescription: '', bodyEdited: true,
          })}
        />

        <div className="respond__buttons">
          <IconButton
            aria-label="Add bold"
            className={this.state.bold ? 'btn active' : 'btn'}
            onClick={() => this.toggleFormat('bold')}
          >
            <span className="icon icon-bold" title="bold"/>
          </IconButton>
          <IconButton
            aria-label="Add italic"
            className={this.state.italic ? 'btn active' : 'btn'}
            onClick={() => this.toggleFormat('italic')}
          >
            <span className="icon icon-italic" title="italic"/>
          </IconButton>
          <IconButton
            aria-label="Add underline"
            className={this.state.underline ? 'btn active' : 'btn'}
            onClick={() => this.toggleFormat('underline')}
          >
            <span className="icon icon-underline" title="underline"/>
          </IconButton>
          <IconButton
            aria-label="Add mention"
            className="btn"
            onClick={() => {
              this.setState({ showEmojiPicker: false });
              this.startMention();
          }}>
            <span className="icon icon-at" title="mention" />
          </IconButton>
          <IconButton
            aria-label="Add emoji"
            className={showEmojiPicker ? 'btn active' : 'btn'}
            onClick={() => this.setState({ showEmojiPicker: !showEmojiPicker })}
          >
            <span className="icon icon-smile" title="emoji" />
          </IconButton>
          {!previewImageUrl &&
            <RespondImageUpload
              uid="image-upload-edit-a-reply"
              onUploadSuccess={(previewImageHash, previewImageUrl) => {
                this.setState({ previewImageHash, previewImageUrl, bodyEdited: true });
                trackFootfallEvent(footfallEventTypes.CLICKED, {clickType: 'image uploaded'});
                showModal({
                  modalType: ModalType.REPLY_IMAGE_DESCRIPTION,
                  modalProps: {
                    imageDescription: previewImageDescription,
                    onAccept: (newDescription) => this.setState({ previewImageDescription: newDescription }),
                  },
                });
              }}
            />
          }
          <ReplyButton
            disabled={!bodyEdited || submitDisabled}
            text={i18n.t('Edit response')}
            onClick={this.onEdit}
          />
        </div>
        {showEmojiPicker && <EmojiPicker onSelect={this.addEmoji} />}
      </Fragment>
    );
  }

  renderListItems(responses) {
    const currentResponse = this.props.response;
    const inReplyToUsername = !currentResponse.author.isDeleted && !currentResponse.author.isHidden
      ? currentResponse.author.username
      : i18n.t('Hidden');
    return responses.map((response) => {
      return (
        <PostResponse
          community={this.props.community}
          currentUser={this.props.currentUser}
          editPostReply={this.props.editPostReply}
          indent={this.props.indent + 1}
          inReplyToUsername={inReplyToUsername}
          key={response.id}
          response={response}
          showModal={this.props.showModal}
          showSignupModal={this.props.showSignupModal}
          signupContext={this.props.signupContext}
          superPost={this.props.superPost}
        />
      );
    });
  }

  render() {
    const { id, indent, response, superPost, community, currentUser, inReplyToUsername } = this.props;
    const author = response.author;
    const { editing, responding } = this.state;
    const dateCreatedId = 'dateCreated' + response.id;
    const isActive = !!currentUser;
    const isCurrentUserPost = isActive && author.id === currentUser.userId;
    const responses = this.renderListItems(response.responses || []);
    const postIdent = indent - 1;

    const className = classNames({
      'post-response-item': true,
      'top-level-comment': postIdent === 0,
      'sub-comment': postIdent >= 1 && postIdent < 3,
      'full-indent': postIdent >= 3,
    });
    const canReport = userHasPermission(currentUser, 'SendReportAbuse');

    if (!!response.isDeleted && !responses.length) return null;

    if (response.isDeleted) {
      return (
        <div
          className={className}
          id={id}
          key={id}
        >
          <UserLink
            avatar={true}
            avatarSize="small"
            className="userlink-avatar-link"
            user={{
              ...author,
              username: 'Hidden',
            }}
          />
          <span className="post-response__deleted">{i18n.t('This reply has been deleted')}</span>
          {responses}
        </div>
      );
    } else {
      return (
        <div
          className={className}
          id={`post-resp-${response.id}`}
          key={id}
        >
          <ResponseHeader
            author={author}
            canCancel={editing ? true : false}
            dateCreated={response.dateCreated}
            dateCreatedId={dateCreatedId}
            inReplyToUsername={inReplyToUsername}
            isActive={isActive}
            signupContextSection={this.props.signupContext.section}
            onCancel={() => this.setState({
              bodyEdited: false,
              editing: false,
              previewImageUrl: this.props.response.imagePreview,
              previewImageDescription: this.props.response.imageDescription,
            })}
          />

          {editing
            ? this.renderEditBox()
            : (
              <Fragment>
                {getElement(response.enriched.body, false, false, isActive)}
                <PostImage
                  communityId={community.communityId}
                  fullscreen={true}
                  post={response}
                />
                <div className="post-media-info">
                  {superPost && !superPost.isLocked &&
                    <Button
                      className="reply"
                      size="sm"
                      onClick={this.onReplyClick}
                    >
                      {i18n.t('Reply')}
                    </Button>
                  }
                  <LikeButton
                    community={community}
                    currentUser={currentUser}
                    isActive={!!currentUser}
                    isDisabled={isCurrentUserPost}
                    noText={true}
                    post={response}
                    signupContext={{section: this.props.signupContext.section}}
                    size="sm"
                    superPost={superPost}
                  />
                  {(!currentUser || canReport) &&
                    <ReportButton
                      size="sm"
                      onClick={this.onReportClick}
                    >
                      {i18n.t('Report')}
                    </ReportButton>
                  }
                  {!!currentUser &&
                    <ActionsDropdown
                      buttonText={i18n.t('More')}
                      canPin={false}
                      color="actions"
                      post={response}
                      size="sm"
                      superPost={superPost}
                      onEdit={() => this.setState({editing: true})}
                    />
                  }
                </div>
              </Fragment>
          )}

          {responding &&
            <Respond
              autoFocus={true}
              canCancel={true}
              community={community}
              currentUser={currentUser}
              post={response}
              respondImageUploadUid="image-upload-reply-to-a-reply"
              signupContext={{section: this.props.signupContext.section}}
              superPost={superPost}
              onCancel={() => this.setState({ responding: false })}
            />
          }

          {responses}
        </div>
      );
    }
  }
}

const mapStateToProps = state => ({
  community: state.context.currentCommunity,
  currentUser: state.context.currentUser,
});

const actions = {
  editPostReply,
  showModal,
  showSignupModal,
};

export default connect(mapStateToProps, actions)(PostResponse);

const ReportButton = styled.button`
  ${textLinkPrimary};
  background: none;
  border: none;
  padding: 6px 5px 6px 5px;
`;
const ReplyButton = styled(Button)`
  margin-left: auto;
`;
const IconButton = styled.button`
  margin: 0;
  padding: 0 10px;
  border: none;
  color: ${props => props.theme.colorGreyDark};
  font-size: 23px;
  transition: none;

  // iOS hover doesn't turn off
  &:hover { // set hover to default colour
    color: ${props => props.theme.colorGreyDark};
  }
  @media (hover: hover) { // only use hover colour if supported by browser
    &:hover {
      color: ${props => props.theme.colorBlue};
    }
  }
  &.active {
    color: ${props => props.theme.colorBlue};
  }

  @media (${props => props.theme.underScreenSmall}) {
    padding: 0 9px;
  }
`;
